/**
 * Cloudflare Turnstile for the Awards ballot.
 *
 * The script loads on first use. Tokens are single-use, so every request gets
 * a fresh widget: token() renders one into the given slot (or returns the one
 * already being prepared there) and reset() throws it away after an attempt.
 * With appearance "interaction-only" the widget is invisible unless Cloudflare
 * needs the visitor to click.
 */

let scriptPromise = null;

function loadScript(src) {
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      if (window.turnstile) {
        resolve(window.turnstile);
        return;
      }

      const script = document.createElement('script');
      script.src = `${src}${src.includes('?') ? '&' : '?'}render=explicit`;
      script.async = true;
      script.onload = () => resolve(window.turnstile);
      script.onerror = () => {
        scriptPromise = null;
        script.remove();
        reject(new Error('Turnstile failed to load'));
      };
      document.head.appendChild(script);
    });
  }

  return scriptPromise;
}

/**
 * @param {Object} options
 * @param {string} options.siteKey Public site key; empty disables the check.
 * @param {string} options.script  Turnstile api.js URL.
 */
export default function createCaptcha({ siteKey, script }) {
  let widget = null;
  let pending = null;
  let slot = null;

  function reset() {
    if (widget !== null && window.turnstile) {
      window.turnstile.remove(widget);
    }

    widget = null;
    pending = null;
    slot = null;
  }

  /**
   * @param {HTMLElement} element Container for the widget.
   * @return {Promise<string>} Token.
   */
  function token(element) {
    if (!siteKey) {
      return Promise.resolve('');
    }

    if (pending && slot === element) {
      return pending;
    }

    reset();
    slot = element;

    const promise = loadScript(script).then((turnstile) => new Promise((resolve, reject) => {
      // The slot may have been reset while the script was loading.
      if (pending !== promise) {
        reject(new Error('Turnstile reset'));
        return;
      }

      widget = turnstile.render(element, {
        sitekey: siteKey,
        appearance: 'interaction-only',
        // Match the Swedish page instead of the browser language.
        language: 'sv',
        'refresh-expired': 'manual',
        callback: resolve,
        'error-callback': () => {
          reject(new Error('Turnstile error'));
          return true;
        },
        'timeout-callback': () => reject(new Error('Turnstile timeout')),
        'expired-callback': () => {
          if (pending === promise) {
            reset();
          }
        },
      });
    }));

    pending = promise;
    promise.catch(() => {
      if (pending === promise) {
        reset();
      }
    });

    return promise;
  }

  return {
    token,
    reset,
    // Warm up in the background so the token is ready on submit.
    prepare(element) {
      token(element).catch(() => {});
    },
  };
}
